import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MdOutlineDesktopWindows, MdFactCheck } from "react-icons/md";
import { useScrollContainerWheel } from "../../../utils/useScrollContainerWheel";
import Dashboard from "../images/dashboard.png";
import ContentHelper from "../images/contentHelper.png";
import LiveSessions from "../images/liveSessions.png";
import MasterData from "../images/masterData.png";
import Reports from "../images/reports.png";
import Tests from "../images/tests.png";
import Tickets from "../images/tickets.png";
import Users from "../images/users.png";
import StudentIcon from "../images/Student.png";

interface subMenu {
  label: string;
  path?: string;
  active?: string[];
  onClick?: () => void;
}

interface Menu {
  icon: string | React.ReactNode;
  label: string;
  path?: string;
  active?: string[];
  subMenu?: subMenu[];
  paths?: string[];
  onhover?: string;
}

interface SidebarProps {
  show: boolean;
  toggleSidebar: () => void;
}

const sidebarItems: Menu[] = [
  { icon: Dashboard, label: "Dashboard", path: "/dashboard" , onhover: "Dashboard"},
  {
    icon: MasterData,
    label: "Master Data",
    onhover: "Master Data",
    subMenu: [
      { label: "Track", path: "admin/track" },
      { label: "Course Creation", path: "admin/courses" },
      { label: "College Creation", path: "admin/colleges" },
      { label: "Branch Creation", path: "admin/branches" },
      { label: "Course Configuration", path: "admin/course-configuration" },
      { label: "Course Test Selection", path: "admin/course-test-selection" },
      { label: "Company", path: "admin/company" },
      {
        label: "Batch Creation",
        path: "admin/batches",
        active: ["admin/batches", "admin/batches/user", "admin/batches/student"],
      },
      { label: "Batch Calendar", path: "admin/day-wise-card", active:["admin/day-wise-card","admin/batch-calendar","admin/day-wise-group","admin/edit-day-wise","admin/edit-batch"]  },
      { label: "Project Configuration", path: "admin/project-configuration" },
      { label: "Rules", path: "admin/rules" },
      {label:"Course Batch Status",path:"admin/course-batch-status"}
    ],
    paths: [
      "admin/courses",
      "admin/colleges",
      "admin/branches",
      "admin/batches",
      "admin/batches/user",
      "admin/batches/student",
      "admin/rules",
      "admin/course-configuration",
      "admin/course-live-sessions",
      "admin/course-test-selection",
      "admin/batch-calendar",
      "admin/project-configuration",
      "admin/project-batch-mapping",
      'admin/course-batch-status'
    ],
  },
  {
    icon: LiveSessions,
    label: "Live Sessions",
    onhover: "Live Sessions",
    subMenu: [
      {
        label: "View Sessions",
        path: "admin/view-sessions",
        active: ["admin/view-sessions"],
      },
      {
        label: "Assign Sessions",
        path: "admin/assign-sessions",
        active: ["admin/assign-sessions"],
      },
      {
        label: "Session Report",
        path: "admin/trainers-report",
        active: ["admin/trainers-report", "admin/session-report", "admin/session-analytics"],
      },
      {
        label: "LiveKit Monitor",
        path: "admin/livekit-monitor",
        active: ["admin/livekit-monitor"],
      },
    ],
    paths: [
      "admin/view-sessions",
      "admin/assign-sessions",
      "admin/trainers-report",
      "admin/livekit-monitor",
      "admin/create-session",
      "admin/live-session",
      "admin/add-students",
      "admin/session-participants",
      "admin/session-report",
      "admin/session-analytics",
      "admin/live-attendance",
    ],
  },
  {
    icon: <MdFactCheck size={25} color="#333" aria-hidden />,
    label: "Attendance",
    path: "admin/attendance/setup",
    onhover: "Attendance",
    paths: [
      "admin/attendance/setup",
      "admin/attendance/live",
      "admin/attendance/report",
    ],
  },
  {
    icon: Tests,
    label: "Tests",
    onhover: "Tests",
    subMenu: [
      {
        label: "Create",
        path: "admin/test/create",
        active: ["admin/test/create", "admin/test-creation"],
      },
      {
        label: "Configure",
        path: "admin/test/violation-policies",
        active: ["admin/test/violation-policies"],
      },
    ],
    paths: [
      "admin/test",
      "admin/test/create",
      "admin/test/configure",
      "admin/test/violation-policies",
      "admin/test/reports",
      "admin/test-creation",
    ],
  },
  {
    icon: Reports,
    label: "Reports",
    path: "admin/reports",
    onhover: "Reports",
  },
  {
    icon: ContentHelper,
    label: "Content",
    onhover: "Content",
    subMenu: [
      { label: "Subject", path: "admin/subject" },
      { label: "Topic", path: "admin/topic" },
      { label: "Sub Topic", path: "admin/sub-topic" },
      { label: "Project", path: "admin/project" },
    ],
    paths: ["admin/subject", "admin/topic", "admin/sub-topic", "admin/project"],
  },
  {
    icon: Users,
    label: "Student Profile",
    path: "admin/student-profile-approval",
    onhover: "Student Profile",
    paths: ["admin/student-profile-approval"],
  },
  {
    icon: StudentIcon,
    label: "Student Placement",
    path: "admin/student-placement",
    onhover: "Student Placement",
    paths: ["admin/student-placement"],
  },
  { icon: Tickets, label: "Tickets", path: "admin/tickets", paths: ["admin/tickets"], onhover: "Tickets" },
  {
    icon: <MdOutlineDesktopWindows size={25} color="#333" aria-hidden />,
    label: "Desktop App",
    onhover: "Desktop App",
    subMenu: [
      { label: "Releases", path: "admin/desktop-releases" },
      { label: "Diagnostics logs", path: "admin/desktop-diagnostics" },
    ],
    paths: ["admin/desktop-releases", "admin/desktop-diagnostics"],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ show, toggleSidebar }) => {
  
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useScrollContainerWheel<HTMLDivElement>();
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [activeMainMenu, setActiveMainMenu] = useState<string | null>(null);
  const pathname = location.pathname;

  useEffect(() => {
    if (!sessionStorage.getItem("Admin")){
      navigate('/')
    }
  }, [location, navigate]);

  useEffect(() => {
    const pathnameWithoutSlash = pathname.slice(1);
    sidebarItems.forEach((item) => {
      const matches =
        item.paths?.some(
          (p) => pathnameWithoutSlash === p || pathnameWithoutSlash.startsWith(`${p}/`)
        ) ?? false;
      if (matches || item.paths?.includes(pathname) || item.paths?.includes(pathnameWithoutSlash)) {
        setActiveMainMenu(item.label);
        setOpenSubMenu(item.label);
      }
    });
  }, [pathname]);

  // useEffect (() => {
  //   if(!show){
  //     setOpenSubMenu(null)
  //   }
  // },[show])

  useEffect(() => {
    const storedActiveMenu = sessionStorage.getItem("activeAdminMainMenu");
    if (storedActiveMenu) {
      setActiveMainMenu(storedActiveMenu);
    }
  }, []);

  useEffect(() => {
    if (activeMainMenu) {
      sessionStorage.setItem("activeAdminMainMenu", activeMainMenu);
    }
  }, [activeMainMenu]);


  const toggleSubMenu = (menu: string) => {
    setOpenSubMenu((prev) => (prev === menu ? null : menu));
  };

  const isSubMenuActive = (subItem: subMenu) => {
    const p = location.pathname.slice(1);
    const full = `${p}${location.search || ""}`;
    if (subItem.active) {
      return subItem.active.some((a) => p === a || p.startsWith(`${a}/`) || full === a);
    }
    if (subItem.path?.includes("?")) {
      return full === subItem.path;
    }
    return location.pathname === `/${subItem.path}`;
  };
  

  return (
    <div
      ref={scrollRef}
      className="d-flex flex-column bg-light shadow"
      style={{
        width: show ? "180px" : "60px",
        height: "100vh",
        // transition: "width 0.2s ease-in-out",
        position: "fixed",
        top: "0",
        left: "0",
        zIndex: "1000",
        overflowX: "hidden",
        overflowY: "auto",
        overscrollBehavior: "contain",
        touchAction: "pan-y",
        borderRight: "1px solid #ccc",
      }}
    >
      <header
        className="d-flex"
        style={{ cursor: "pointer" }}
        onClick={toggleSidebar}
      >
        <span
          className="mb-0 fs-2 ps-2 fw-bolder text-start"
        >
          EU
        </span>
      </header>

      <div
        className="mt-3"
        style={{
          fontSize: "14px",
          cursor: "pointer",
        }}
      >
        {sidebarItems.map((item, index) => (
          <div key={index}>
            <div
              className="d-flex align-items-center p-2 px-3 mb-1 hover-bg-primary"
              style={{
                cursor: "pointer",
                borderLeft:
                  activeMainMenu === item.label ? "4px solid black" : "none",
                borderRadius: "4px",
              }}
              title={item.onhover}
              onClick={() => {
                if (item.subMenu) {
                  toggleSubMenu(item.label);
                  if (!show) {
                    toggleSidebar(); 
                  }
                } else if (item.path) {
                  navigate(`/${item.path}`);
                  setActiveMainMenu(item.label);
                  setOpenSubMenu(null);
                }
              }}
            >
              {typeof item.icon === "string" ? (
                <img
                  src={item.icon}
                  alt={item.label}
                  className="me-3"
                  style={{
                    width: "25px",
                  }}
                />
              ) : (
                <span
                  className="me-3 d-inline-flex align-items-center justify-content-center"
                  style={{ width: "25px", minWidth: "25px" }}
                >
                  {item.icon}
                </span>
              )}
              <span className={show ? "d-block" : "d-none"}>{item.label}</span>
            </div>

            {openSubMenu === item.label && item.subMenu && (
              <div className={show ? "d-block" : "d-none"}>
                {item.subMenu.map((subItem, subIndex) => (
                  <div
                    key={subIndex}
                    className="d-flex align-items-center p-1 ps-5 mb-1 hover-bg-primary ms-3"
                    style={{
                      cursor: "pointer",
                      fontSize: "12px",
                      textDecoration: isSubMenuActive(subItem)
                        ? "underline"
                        : "none",
                      textDecorationThickness: isSubMenuActive(subItem)
                        ? "2px"
                        : "none",
                      textDecorationColor: isSubMenuActive(subItem)
                        ? "blue"
                        : "none",
                      borderRadius: "4px",
                    }}
                    onClick={() => {
                      if (subItem.onClick) {
                        subItem.onClick();
                      } else if (subItem.path) {
                        navigate(`/${subItem.path}`);
                        setActiveMainMenu(item.label);
                      }
                    }}
                  >
                    <span className={show ? "d-block" : "d-none"}>
                      {subItem.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div
        className="flex-grow-1"
        style={{
          cursor: "pointer",
        }}
        onClick={toggleSidebar}
      ></div>

    </div>
  );
};

export default Sidebar;
